/**
 * Admin — Hero Slides Manager
 * Lets admins create / edit / re-order / delete the landing-page hero carousel slides
 * (DE / EN / FR / IT) without code deploys.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronLeft, RefreshCw, Plus, Trash2, Edit2, Save, X, Image as ImageIcon,
  ToggleLeft, ToggleRight, ArrowUp, ArrowDown, Eye, EyeOff, Loader2
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { useLanguage } from "../../context/AppContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  title_de: "", title_en: "", title_fr: "", title_it: "",
  subtitle_de: "", subtitle_en: "", subtitle_fr: "", subtitle_it: "",
  cta_label_de: "Mehr erfahren", cta_label_en: "Learn more",
  cta_label_fr: "En savoir plus", cta_label_it: "Scopri di più",
  cta_url: "/register",
  image_url: "",
  active: true,
  order: 99,
};

export default function AdminHeroSlides() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/hero-slides`, { withCredentials: true });
      setSlides(res.data.slides || []);
    } catch (e) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    if (!editing.title_de && !editing.title_en) {
      toast.error("Titel (DE oder EN) ist Pflicht");
      return;
    }
    setSaving(true);
    try {
      if (editing.id && slides.some(s => s.id === editing.id)) {
        await axios.put(`${API}/admin/hero-slides/${editing.id}`, editing, { withCredentials: true });
        toast.success("Slide aktualisiert");
      } else {
        await axios.post(`${API}/admin/hero-slides`, editing, { withCredentials: true });
        toast.success("Slide erstellt");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Diesen Slide endgültig löschen?")) return;
    try {
      await axios.delete(`${API}/admin/hero-slides/${id}`, { withCredentials: true });
      toast.success("Slide gelöscht");
      load();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const toggleActive = async (slide) => {
    try {
      await axios.put(`${API}/admin/hero-slides/${slide.id}`,
        { active: !slide.active }, { withCredentials: true });
      toast.success(slide.active ? "Deaktiviert" : "Aktiviert");
      load();
    } catch (e) {
      toast.error("Fehler");
    }
  };

  const move = async (slide, dir) => {
    const newOrder = (slide.order || 0) + dir;
    try {
      await axios.put(`${API}/admin/hero-slides/${slide.id}`,
        { order: newOrder }, { withCredentials: true });
      load();
    } catch (e) {
      toast.error("Reihenfolge konnte nicht geändert werden");
    }
  };

  return (
    <div
      className="min-h-screen infirmly-dark p-4 sm:p-6"
      style={{ background: "radial-gradient(ellipse at top, #0F2557 0%, #0A1A3E 60%, #050B23 100%)" }}
      data-testid="admin-hero-slides"
    >
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/admin')} className="rounded-full bg-white/[0.05] border-white/15 text-white hover:bg-white/10">
              <ChevronLeft className="w-4 h-4 mr-1" /> Admin
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "Chivo, sans-serif" }}>{t('adminPageHeroSlides')}</h1>
              <p className="text-blue-100/60 text-sm">Slides für die Startseite (DE / EN / FR / IT)</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={load} variant="outline" size="sm" className="rounded-full bg-white/[0.05] border-white/15 text-white hover:bg-white/10">
              <RefreshCw className="w-4 h-4 mr-1" /> Aktualisieren
            </Button>
            <Button onClick={() => setEditing({ ...empty, order: slides.length })} className="bg-[#0044CC] hover:bg-[#0055EE] rounded-full" data-testid="new-slide-btn">
              <Plus className="w-4 h-4 mr-1" /> Neuer Slide
            </Button>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#60a5fa]" /></div>
        ) : slides.length === 0 ? (
          <Card><CardContent className="text-center py-12 text-blue-100/60">Noch keine Slides. Klicken Sie "Neuer Slide".</CardContent></Card>
        ) : (
          <div className="space-y-3">
            {slides.map((slide, i) => (
              <Card key={slide.id} className="overflow-hidden" data-testid={`slide-row-${slide.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4 flex-wrap">
                    {/* Preview */}
                    <div className="w-32 h-20 rounded-xl flex-shrink-0 relative overflow-hidden bg-gradient-to-br from-[#0A1A3E] to-[#0F2557] border border-white/10">
                      {slide.image_url ? (
                        <img src={slide.image_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex items-center justify-center h-full text-blue-100/40">
                          <ImageIcon className="w-6 h-6" />
                        </div>
                      )}
                      <div className="absolute top-1 left-1 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded">#{slide.order ?? i}</div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="text-white font-bold text-sm truncate">{slide.title_de || slide.title_en || "(Ohne Titel)"}</h3>
                        {slide.active ? (
                          <span className="text-[10px] font-bold bg-green-500/20 text-green-300 border border-green-400/30 px-1.5 py-0.5 rounded-full">AKTIV</span>
                        ) : (
                          <span className="text-[10px] font-bold bg-white/10 text-blue-100/50 border border-white/15 px-1.5 py-0.5 rounded-full">INAKTIV</span>
                        )}
                      </div>
                      <p className="text-blue-100/60 text-xs line-clamp-1">{slide.subtitle_de || slide.subtitle_en}</p>
                      <div className="flex items-center gap-3 mt-1 text-[11px] text-blue-100/50">
                        <span>CTA: {slide.cta_label_de || slide.cta_label_en} → {slide.cta_url}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => move(slide, -1)} className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 flex items-center justify-center" title="Nach oben">
                        <ArrowUp className="w-3.5 h-3.5 text-blue-100" />
                      </button>
                      <button onClick={() => move(slide, 1)} className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 flex items-center justify-center" title="Nach unten">
                        <ArrowDown className="w-3.5 h-3.5 text-blue-100" />
                      </button>
                      <button onClick={() => toggleActive(slide)} className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 flex items-center justify-center" title={slide.active ? "Deaktivieren" : "Aktivieren"}>
                        {slide.active ? <Eye className="w-3.5 h-3.5 text-green-300" /> : <EyeOff className="w-3.5 h-3.5 text-blue-100/50" />}
                      </button>
                      <button onClick={() => setEditing(slide)} className="w-8 h-8 rounded-lg bg-blue-500/15 border border-blue-400/30 hover:bg-blue-500/25 flex items-center justify-center" data-testid={`edit-${slide.id}`}>
                        <Edit2 className="w-3.5 h-3.5 text-blue-300" />
                      </button>
                      <button onClick={() => remove(slide.id)} className="w-8 h-8 rounded-lg bg-red-500/15 border border-red-400/30 hover:bg-red-500/25 flex items-center justify-center" data-testid={`delete-${slide.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-300" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Modal */}
        {editing && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="edit-slide-modal">
            <div className="bg-[#0A1A3E] border border-white/15 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="sticky top-0 bg-[#0A1A3E] border-b border-white/10 p-5 flex items-center justify-between z-10">
                <h2 className="text-white font-bold text-lg" style={{ fontFamily: "Chivo, sans-serif" }}>
                  {editing.id && slides.some(s => s.id === editing.id) ? "Slide bearbeiten" : "Neuer Slide"}
                </h2>
                <button onClick={() => setEditing(null)} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center">
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>

              <div className="p-5 space-y-5">
                {/* Image URL */}
                <div>
                  <Label className="text-white">Bild-URL</Label>
                  <Input
                    value={editing.image_url || ""}
                    onChange={e => setEditing({ ...editing, image_url: e.target.value })}
                    placeholder="https://… (optional, leer = Swiss-Map Hintergrund)"
                    data-testid="field-image-url"
                  />
                  {editing.image_url && (
                    <div className="mt-2 rounded-xl overflow-hidden border border-white/10 max-h-32">
                      <img src={editing.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    </div>
                  )}
                </div>

                {/* Titles per language */}
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-[#60a5fa] mb-2">Titel</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[['de', '🇩🇪 Deutsch'], ['en', '🇬🇧 English'], ['fr', '🇫🇷 Français'], ['it', '🇮🇹 Italiano']].map(([lng, lbl]) => (
                      <div key={lng}>
                        <Label className="text-xs text-blue-100/70">{lbl}</Label>
                        <Input
                          value={editing[`title_${lng}`] || ""}
                          onChange={e => setEditing({ ...editing, [`title_${lng}`]: e.target.value })}
                          data-testid={`field-title-${lng}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Subtitles */}
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-[#60a5fa] mb-2">Untertitel</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[['de', '🇩🇪'], ['en', '🇬🇧'], ['fr', '🇫🇷'], ['it', '🇮🇹']].map(([lng, lbl]) => (
                      <div key={lng}>
                        <Label className="text-xs text-blue-100/70">{lbl} {lng.toUpperCase()}</Label>
                        <Textarea
                          value={editing[`subtitle_${lng}`] || ""}
                          onChange={e => setEditing({ ...editing, [`subtitle_${lng}`]: e.target.value })}
                          rows={2}
                          data-testid={`field-subtitle-${lng}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA Labels */}
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-[#60a5fa] mb-2">CTA-Button Text</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[['de', '🇩🇪'], ['en', '🇬🇧'], ['fr', '🇫🇷'], ['it', '🇮🇹']].map(([lng, lbl]) => (
                      <div key={lng}>
                        <Label className="text-xs text-blue-100/70">{lbl} {lng.toUpperCase()}</Label>
                        <Input
                          value={editing[`cta_label_${lng}`] || ""}
                          onChange={e => setEditing({ ...editing, [`cta_label_${lng}`]: e.target.value })}
                          data-testid={`field-cta-${lng}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA URL & order */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-white">CTA URL</Label>
                    <Input
                      value={editing.cta_url || ""}
                      onChange={e => setEditing({ ...editing, cta_url: e.target.value })}
                      placeholder="/register"
                      data-testid="field-cta-url"
                    />
                  </div>
                  <div>
                    <Label className="text-white">Reihenfolge</Label>
                    <Input
                      type="number"
                      value={editing.order ?? 99}
                      onChange={e => setEditing({ ...editing, order: parseInt(e.target.value) || 0 })}
                      data-testid="field-order"
                    />
                  </div>
                </div>

                {/* Active toggle */}
                <button
                  onClick={() => setEditing({ ...editing, active: !editing.active })}
                  className="flex items-center gap-2 text-white"
                  data-testid="field-active-toggle"
                >
                  {editing.active ? <ToggleRight className="w-7 h-7 text-green-400" /> : <ToggleLeft className="w-7 h-7 text-blue-100/40" />}
                  <span className="text-sm">{editing.active ? "Aktiv (auf Startseite sichtbar)" : "Inaktiv (versteckt)"}</span>
                </button>
              </div>

              <div className="sticky bottom-0 bg-[#0A1A3E] border-t border-white/10 p-4 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full bg-white/[0.05] border-white/15 text-white hover:bg-white/10">
                  Abbrechen
                </Button>
                <Button onClick={save} disabled={saving} className="rounded-full bg-[#0044CC] hover:bg-[#0055EE]" data-testid="save-slide-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Speichern
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
